/* Presentations (Build): a deck of slides, grouped by the section of the talk
 * each slide belongs to.
 *
 * Her shape, 2026-09-16: "presentations with sections and it can auto draft a
 * few slides and we can edit them" — slides are the rows, shown under their
 * section headings (docs/presentations-spec.md header). Brill PROPOSES a few
 * slides for a section; she adds them (rule 4); then each slide is a row she
 * edits, or talks through with Brill.
 *
 * Three tables (supabase/0021): presentations → sections → slides, each with
 * its own clerk_user_id, the Call Scripts shape. Everything above
 * `usePresentations` is pure and tested in presentations.test.js.
 *
 * The slide TYPES and how each one is drawn live in deckLayout.js; this file
 * says what each type holds as editable FIELDS, in the shape the shared Block
 * and Drafter read. The content keys are the ones deckLayout draws from.
 */

import { useCallback, useMemo } from "react";
import { createRecordStore } from "./recordStore";
import { offerLine } from "./offerLine";
import { applyLabelledBlock, ordinalWord, parseLabelledBlock, stripLabelledBlock } from "./sectionBlock";
import { blockLabel, fieldWords, isListField, rowsOf, wordCount } from "./websiteSectionKinds";
import { SLIDE_TYPES } from "./deckLayout";
import { brandSkin } from "./brand";

/* ── What a presentation is for ───────────────────────────────────────────────
   A TYPE is a content recipe over the same nine slide types (the spec). It
   decides the sections a new deck starts with. Launch scope is these two. */
export const PRESENTATION_TYPES = [
  {
    id: "masterclass", label: "Masterclass",
    hint: "A teaching talk that opens with a big promise and closes on your program",
    sections: [
      ["Opening", "the hook — a big promise, and why it matters to this room"],
      ["Origin story", "how you found this, told so the room sees themselves in it"],
      ["Secret 1", "the first shift, and the old belief it replaces"],
      ["Secret 2", "the second shift, and the old belief it replaces"],
      ["Secret 3", "the third shift, and the old belief it replaces"],
      ["Stack & close", "what you build together, the offer, and one clear invitation"],
    ],
  },
  {
    id: "weekly_teaching", label: "Weekly Teaching Deck",
    hint: "One week of your program — where they are, this week's lesson, and their homework",
    sections: [
      ["Last week → this week", "where they are, and what today builds on"],
      ["The teaching", "this week's lesson"],
      ["Homework", "what they do before next week — homework, not a pitch"],
    ],
  },
];

export function presentationTypeDef(id) {
  return PRESENTATION_TYPES.find((t) => t.id === id) || null;
}

/* ── Slide fields ─────────────────────────────────────────────────────────────
   One entry per deckLayout type. Every slide also carries SPEAKER NOTES —
   what she says while it is up — stored in content.notes. */
const NOTES = { key: "notes", label: "Speaker notes", type: "prose", guide: "what you say while this slide is up", words: 90 };

const SLIDE_FIELDS = {
  title: [
    { key: "headline", label: "Headline", type: "line", guide: "the talk's name", words: 8 },
    { key: "subtitle", label: "Subtitle", type: "line", guide: "the promise, in one line", words: 14 },
    { key: "presenter", label: "Presenter", type: "line", guide: "your name" },
    /* OPTIONAL pictures are drawn only when chosen — no placeholder — so the
       read view skips them empty and the field says so (fields.jsx). */
    { key: "image", label: "Background picture", type: "image", optional: true, guide: "optional — sits behind the title",
      emptyHint: "Optional. Leave it empty and the title slide stays plain." },
  ],
  divider: [
    { key: "number", label: "Number", type: "line", guide: "01, 02…" },
    { key: "title", label: "Section title", type: "line", guide: "names this part of the talk", words: 6 },
  ],
  statement: [
    { key: "line", label: "Statement", type: "line", guide: "one idea that needs room to land", words: 12 },
  ],
  list: [
    { key: "heading", label: "Heading", type: "line", guide: "names the framework or steps", words: 6 },
    {
      key: "items", label: "Items", itemLabel: "Item", type: "list", block: "ITEM", guide: "three to five",
      parts: [
        { key: "label", label: "Label", type: "line", guide: "a word or two", words: 3 },
        { key: "detail", label: "Detail", type: "line", guide: "one short line", words: 10 },
      ],
    },
  ],
  image_text: [
    { key: "heading", label: "Heading", type: "line", guide: "what the picture proves", words: 6 },
    { key: "body", label: "Text", type: "prose", guide: "two or three lines", words: 35 },
    /* THE PICTURE ITSELF. deckLayout has drawn `content.image` since this slide
       type existed, and nothing ever offered a way to fill it — the layout and
       the editor quietly disagreed, which is what "where images come from is
       still open" meant in the spec. It is a link: the export already takes a
       URL (pptxgenjs `path`) or a data URI, so anything hosted works, hers in
       GHL's media library included. Nothing here requires GHL. */
    { key: "image", label: "Image", type: "image", guide: "paste a link to the picture" },
  ],
  // The same three fields, picture on the right — so changing between the two
  // is a flip that loses nothing.
  text_image: [
    { key: "heading", label: "Heading", type: "line", guide: "what the picture proves", words: 6 },
    { key: "body", label: "Text", type: "prose", guide: "two or three lines", words: 35 },
    { key: "image", label: "Image", type: "image", guide: "paste a link to the picture" },
  ],
  image_full: [
    { key: "image", label: "Image", type: "image", guide: "paste a link to the picture" },
    { key: "heading", label: "Words over the picture", type: "line", guide: "optional — a few words on a band across the bottom", words: 8 },
    { key: "line", label: "Supporting line", type: "line", guide: "optional — one short line under them", words: 12 },
  ],
  gallery: [
    { key: "heading", label: "Heading", type: "line", guide: "what these pictures show together", words: 6 },
    { key: "image1", label: "Picture 1", type: "image", guide: "paste a link to the picture" },
    { key: "caption1", label: "Caption 1", type: "line", guide: "one short line under the first picture", words: 8 },
    { key: "image2", label: "Picture 2", type: "image", guide: "paste a link to the picture" },
    { key: "caption2", label: "Caption 2", type: "line", guide: "one short line under the second picture", words: 8 },
    { key: "image3", label: "Picture 3", type: "image", optional: true, guide: "paste a link to the picture",
      emptyHint: "Leave the third picture and caption empty for a row of two." },
    { key: "caption3", label: "Caption 3", type: "line", guide: "one short line under the third — leave empty for a row of two", words: 8 },
  ],
  quote: [
    { key: "quote", label: "Quote", type: "prose", guide: "their words, exactly — never invented", words: 30 },
    { key: "attribution", label: "Who said it", type: "line", guide: "name, or how they're described" },
    { key: "photo", label: "Their photo", type: "image", optional: true, guide: "optional — their headshot",
      emptyHint: "Optional. With a photo it shows round above the quote; without, the quotation mark stays." },
  ],
  stat: [
    { key: "number", label: "Number", type: "line", guide: "the figure — only a real one", words: 2 },
    { key: "line", label: "Supporting line", type: "line", guide: "what the number means", words: 12 },
  ],
  old_new: [
    {
      key: "pairs", label: "Before and after", itemLabel: "Pair", type: "list", block: "PAIR", guide: "two or three",
      parts: [
        { key: "old", label: "Old way", type: "line", guide: "what wasn't working", words: 10 },
        { key: "new", label: "New way", type: "line", guide: "what changes", words: 10 },
      ],
    },
  ],
  closing: [
    { key: "headline", label: "Headline", type: "line", guide: "the ask, as a question or a call", words: 6 },
    { key: "line", label: "Supporting line", type: "line", guide: "what happens next", words: 18 },
    { key: "cta", label: "Button text", type: "line", guide: "an action", words: 4 },
    { key: "link", label: "Link", type: "line", guide: "where the button goes — you add it" },
  ],
};

/** The kind the shared Block and Drafter read: a label and its fields. */
/* What the Library fills on a slide, by slide type. A KIND-level spec because
   a quote slide takes TWO fields from one testimonial, where a website section
   takes a row in a list. Word for word, and permission required — a deck is
   shown to a room (her call, 2026-09-17). */
export const SLIDE_LIBRARY = {
  quote: {
    kind: "testimonial",
    requirePermission: true,
    // Her headshot comes with her words when the testimonial has one; a
    // testimonial without leaves any photo already on the slide alone.
    fill: (it) => ({ quote: it.text || "", attribution: it.who || "", ...(String(it.photo || "").trim() ? { photo: it.photo.trim() } : {}) }),
  },
};

export function slideKind(type) {
  const def = SLIDE_TYPES.find((t) => t.id === type) || SLIDE_TYPES.find((t) => t.id === "statement");
  return { id: def.id, label: def.label, fields: [...SLIDE_FIELDS[def.id], NOTES], library: SLIDE_LIBRARY[def.id] || null };
}

export function emptySlideContent(type) {
  const out = {};
  for (const f of slideKind(type).fields) out[f.key] = isListField(f) ? [] : "";
  return out;
}

/** A slide's heading in a list: its own words where it has some, else its type. */
export function slideTitle(slide) {
  const c = slide?.content || {};
  /* Only THIS type's fields: a slide whose type changed still carries the
     words the new type has no place for (changeSlideType), and a title read
     from those would name the slide after words nobody can see on it. */
  const own = new Set(slideKind(slide?.slideType).fields.map((f) => f.key));
  const first = ["headline", "title", "heading", "line", "quote", "number", "caption1"].filter((k) => own.has(k)).map((k) => String(c[k] || "").trim()).find(Boolean);
  return first || slideKind(slide?.slideType).label;
}

/* ── Changing a slide's type ──────────────────────────────────────────────────
   Her ask, 2026-09-23 and again 2026-10-01: a slide added as the wrong kind
   should not have to be deleted and rewritten. Each field of the new type takes
   the first of these the old slide had words in, so a heading stays a heading
   whatever it was called. NOTHING IS THROWN AWAY: words the new type has no
   place for stay in the slide's content, unseen, and come back if she changes
   it back. Speaker notes always come along. */
const CARRY = {
  headline: ["headline", "heading", "title", "line"],
  heading: ["heading", "headline", "title", "line"],
  title: ["title", "heading", "headline", "line"],
  line: ["line", "heading", "headline", "title"],
  subtitle: ["subtitle", "body", "line"],
  body: ["body", "subtitle", "line"],
  quote: ["quote", "body", "line"],
  image: ["image", "image1", "photo"],
  image1: ["image1", "image"],
  photo: ["photo", "image"],
  caption1: ["caption1", "line"],
};

const hasWords = (v) => (Array.isArray(v) ? v.length > 0 : String(v ?? "").trim() !== "");

function carry(content, fromType, toType) {
  const from = new Set(slideKind(fromType).fields.map((f) => f.key));
  const out = { ...content };
  const used = new Set();
  for (const f of slideKind(toType).fields) {
    if (f.key === "notes") continue;
    /* What she can SEE on the old slide wins. Only then does a leftover from
       an earlier change come back — under its own key, never another's — so
       a line she edited after a change is not beaten by the words it
       replaced. */
    const source = (CARRY[f.key] || [f.key]).find((k) => from.has(k) && !used.has(k) && hasWords(content[k]))
      || (hasWords(content[f.key]) && !used.has(f.key) ? f.key : null);
    if (source) { out[f.key] = content[source]; used.add(source); }
    else out[f.key] = isListField(f) ? [] : "";
  }
  return { out, used };
}

export function changeSlideType(content = {}, fromType, toType) {
  return carry(content || {}, fromType, toType).out;
}

/** The labels of what the old slide showed that the new type will not —
 *  said before she commits, so a change that hides a list is never silent. */
export function wordsLeftBehind(content = {}, fromType, toType) {
  const { used } = carry(content || {}, fromType, toType);
  return slideKind(fromType).fields
    .filter((f) => f.key !== "notes" && hasWords(content?.[f.key]) && !used.has(f.key))
    .map((f) => f.label);
}

/* ── Words and state ──────────────────────────────────────────────────────────
   Counted over the slide's own fields, notes included — the notes are the
   script, and the script is most of the words she writes. */
export function slideWords(slide) {
  const content = slide?.content || {};
  let n = 0;
  for (const f of slideKind(slide?.slideType).fields) {
    /* A link is not words: counting it would make an unwritten slide with a
       picture on it read as written, and the counts are what the rail, the
       dots and "how far along is this" all run on. */
    if (f.type === "image") continue;
    if (!isListField(f)) { n += wordCount(content[f.key]); continue; }
    for (const row of rowsOf(content, f)) {
      n += typeof row === "string" ? wordCount(row) : Object.values(row || {}).reduce((m, v) => m + wordCount(v), 0);
    }
  }
  return n;
}

export function deckWords(slides = []) {
  return (slides || []).reduce((n, s) => n + slideWords(s), 0);
}

export const DECK_STATE_LABEL = { done: "Done", draft: "Draft", empty: "Not written" };

/** The same three words, in the same order, as every other record. Done is hers. */
export function deckState(presentation, slides = []) {
  if (presentation?.status === "done") return "done";
  return (slides || []).some((s) => slideWords(s) > 0) ? "draft" : "empty";
}

export function slideState(slide) {
  return slideWords(slide) > 0 ? "draft" : "empty";
}

export function presentationLabel(p) {
  return String(p?.name || "").trim() || "Untitled presentation";
}

/** Sections in order, each with its slides in order. Slides whose section is
 *  gone land in a trailing "Unsorted" group rather than vanishing. */
export function groupSlides(sections = [], slides = []) {
  const byOrder = (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
  const secs = [...(sections || [])].sort(byOrder);
  const ids = new Set(secs.map((s) => s.id));
  const groups = secs.map((s) => ({ section: s, slides: (slides || []).filter((x) => x.sectionId === s.id).sort(byOrder) }));
  const loose = (slides || []).filter((x) => !ids.has(x.sectionId)).sort(byOrder);
  if (loose.length) groups.push({ section: null, slides: loose });
  return groups;
}

/** The deck as deckExport wants it: every slide in section order, its notes
 *  lifted out of content. */
export function deckSlides(sections = [], slides = []) {
  return groupSlides(sections, slides).flatMap((g) => g.slides).map((s) => {
    const { notes, ...content } = s.content || {};
    return { type: s.slideType, content, notes: notes || "" };
  });
}

/** The skin a deck wears: her colours from Me → Brand, her locked My Voice &
 *  Branding for anything Me leaves empty (`brandSkin`, the ONE reader every
 *  tool shares — "Me wins", 2026-10-12), then this deck's own overrides on
 *  top. `read` is kept so the screen can say what came from where, and what
 *  could not be read. */
export function deckSkin(presentation, voiceOutputs = {}, brand = null) {
  const { skin, read } = brandSkin({ brand, voiceOutputs });
  const own = Object.fromEntries(Object.entries(presentation?.skin || {}).filter(([, v]) => String(v || "").trim()));
  return { skin: { ...skin, ...own }, read };
}


/* ── Writing with Brill ───────────────────────────────────────────────────────
   Two blocks, one reader. ONE SLIDE (or every slide of a section) comes back
   in <<<SLIDE_DRAFTED>>> under that slide's labels. A PROPOSAL — new slides
   for a section — comes back in <<<SLIDES_PROPOSED>>>, each slide opened by a
   `SLIDE: <type>` line. Field lists are read from SLIDE_FIELDS; api/_lib/prompts.js
   carries no copy. */

export const SLIDE_BLOCK = /<<<SLIDE_DRAFTED>>>([\s\S]*?)<<<SLIDE_END>>>/;
export const PROPOSAL_BLOCK = /<<<SLIDES_PROPOSED>>>([\s\S]*?)<<<SLIDES_END>>>/;
const WHOLE = /^([\s\S]*)$/;

function specLine(f) {
  const n = fieldWords(f);
  const guide = n ? `${f.guide} (about ${n} words)` : f.guide;
  if (!isListField(f)) return `${blockLabel(f)}: ${f.label.toLowerCase()} — ${guide}`;
  const parts = f.parts.map((p) => `${p.label.toLowerCase()} (${fieldWords(p) ? `${p.guide}, about ${fieldWords(p)} words` : p.guide})`).join(" | ");
  return `${blockLabel(f)}: ${parts}. Separate the parts with " | ", and repeat this line for each ${f.itemLabel.toLowerCase()} (${f.guide}).`;
}

export function slideSpecLines(type) {
  /* AN IMAGE IS NOT SOMETHING BRILL CAN WRITE. Asked for a link it would
     invent a plausible one, which is the worst kind of wrong: a URL that looks
     right, 404s quietly, and leaves a slide with a broken picture where the
     placeholder used to be. So the field exists for her and is never put in
     front of the model. */
  return slideKind(type).fields.filter((f) => f.type !== "image").map(specLine);
}

function deckLines(presentation, offers) {
  const type = presentationTypeDef(presentation?.type);
  const offer = (offers || []).find((o) => o.id === presentation?.offerId);
  return [
    `Presentation: ${presentationLabel(presentation)}`,
    `What it is: ${type ? `${type.label} — ${type.hint}` : "not set"}`,
    `Presenter: ${String(presentation?.presenter || "").trim() || "not set"}`,
    `Offer it leads to: ${offerLine(offer)}`,
  ];
}

function slideSummary(s) {
  const kind = slideKind(s.slideType);
  const c = s.content || {};
  const bits = kind.fields.filter((f) => f.key !== "notes").map((f) => {
    const v = isListField(f)
      ? rowsOf(c, f).map((r) => (typeof r === "string" ? r : Object.values(r || {}).filter(Boolean).join(" / "))).filter(Boolean).join("; ")
      : String(c[f.key] || "").trim();
    return v ? `${f.label}: ${v}` : "";
  }).filter(Boolean);
  return `${kind.label}${bits.length ? ` — ${bits.join(" · ")}` : " (not written)"}`;
}

function outline(sections, slides) {
  return groupSlides(sections, slides).flatMap((g) => [
    `## ${g.section ? g.section.name || "Untitled section" : "Unsorted"}${g.section?.brief ? ` (${g.section.brief})` : ""}`,
    ...(g.slides.length ? g.slides.map((s) => `- ${slideSummary(s)}`) : ["- (no slides yet)"]),
  ]);
}

/** One slide: the deck, where the slide sits, its labels, what it says now. */
export function buildSlideContext({ presentation, section, slide, sections = [], slides = [], offers = [] }) {
  const c = slide?.content || {};
  return [
    "[SLIDE_CONTEXT — read only, do not reference as a conversation]",
    ...deckLines(presentation, offers),
    `Section: ${section?.name || "none"}${section?.brief ? ` — ${section.brief}` : ""}`,
    `This slide: ${slideKind(slide?.slideType).label}. Keep its type; rewrite its words.`,
    "",
    "Fields to fill, one per line, using EXACTLY these labels:",
    ...slideSpecLines(slide?.slideType),
    "",
    "What this slide says now:",
    ...slideKind(slide?.slideType).fields.map((f) => `${blockLabel(f)}: ${isListField(f) ? (rowsOf(c, f).map((r) => (typeof r === "string" ? r : Object.values(r).join(" | "))).join(" / ") || "(empty)") : (String(c[f.key] || "").trim() || "(empty)")}`),
    "",
    "The whole deck, for fit — do not rewrite other slides:",
    ...outline(sections, slides),
  ].join("\n");
}

/** Every slide of a section, each field under a label unique to its place:
 *  SLIDE_TWO_HEADLINE. `key` is `${slideId}::${field}`. */
export function sectionSlideFields(slides = []) {
  const out = [];
  (slides || []).forEach((s, i) => {
    for (const f of slideKind(s.slideType).fields) {
      out.push({ ...f, key: `${s.id}::${f.key}`, block: `SLIDE_${ordinalWord(i)}_${blockLabel(f)}`, slideId: s.id, field: f.key });
    }
  });
  return out;
}

export function buildSectionContext({ presentation, section, sections = [], slides = [], offers = [] }) {
  const mine = groupSlides(sections, slides).find((g) => g.section?.id === section?.id)?.slides || [];
  const fields = sectionSlideFields(mine);
  return [
    "[SECTION_SLIDES_CONTEXT — read only, do not reference as a conversation]",
    "Scope: EVERY SLIDE IN THIS SECTION. Rewrite the ones you have words for, each under its own labels, all inside ONE <<<SLIDE_DRAFTED>>> block. Keep every slide's type.",
    ...deckLines(presentation, offers),
    `Section: ${section?.name || "Untitled section"}${section?.brief ? ` — ${section.brief}` : ""}`,
    "",
    ...mine.flatMap((s, i) => [
      `— Slide ${i + 1} of ${mine.length}: ${slideKind(s.slideType).label}`,
      ...slideSpecLines(s.slideType).map((l) => `  SLIDE_${ordinalWord(i)}_${l}`),
    ]),
    "",
    "Labels to answer under (omit any slide you are not changing):",
    ...fields.map((f) => f.block),
    "",
    "The whole deck, for fit:",
    ...outline(sections, slides),
  ].join("\n");
}

/** What Brill is told when she asks for NEW slides for a section. */
export function buildProposalContext({ presentation, section, sections = [], slides = [], offers = [], count = 4 }) {
  return [
    "[PROPOSE_SLIDES — read only, do not reference as a conversation]",
    ...deckLines(presentation, offers),
    `Section to draft: ${section?.name || "Untitled section"}${section?.brief ? ` — ${section.brief}` : ""}`,
    `Propose about ${count} NEW slides for this section, in order. They are added AFTER any slides the section already has.`,
    "",
    "Slide types, and the labels each one takes (use EXACTLY these):",
    ...SLIDE_TYPES.filter((t) => t.id !== "title").map((t) => `SLIDE: ${t.id} — ${t.label}\n  ${slideSpecLines(t.id).join("\n  ")}`),
    "",
    "The whole deck so far:",
    ...outline(sections, slides),
  ].join("\n");
}

export function parseSlideBlock(text, slide) {
  return parseLabelledBlock(text, slideKind(slide?.slideType).fields, SLIDE_BLOCK);
}

export function applySlideBlock(content, block, slide) {
  return applyLabelledBlock(content, block, slideKind(slide?.slideType).fields);
}

/** { [slideId]: content } for the section's slides Brill rewrote, or null. */
export function parseSectionSlidesBlock(text, slides = []) {
  const fields = sectionSlideFields(slides);
  const flat = parseLabelledBlock(text, fields, SLIDE_BLOCK);
  if (!flat) return null;
  const out = {};
  for (const f of fields) {
    if (flat[f.key] === undefined) continue;
    (out[f.slideId] = out[f.slideId] || {})[f.field] = flat[f.key];
  }
  return Object.keys(out).length ? out : null;
}

/** New slides Brill proposed: [{ slideType, content }], or null. A slide of a
 *  type this deck does not know is dropped, never guessed at; a slide with no
 *  words is dropped too. */
export function parseProposal(text) {
  const m = PROPOSAL_BLOCK.exec(text || "");
  if (!m) return null;
  const out = [];
  const chunks = m[1].split(/^[ \t*]*SLIDE[ \t*]*:[ \t]*/im).slice(1);
  for (const chunk of chunks) {
    const nl = chunk.indexOf("\n");
    const type = (nl < 0 ? chunk : chunk.slice(0, nl)).trim().toLowerCase().replace(/[^a-z_]/g, "");
    if (!SLIDE_TYPES.some((t) => t.id === type)) continue;
    const content = applyLabelledBlock(emptySlideContent(type), parseLabelledBlock(nl < 0 ? "" : chunk.slice(nl + 1), slideKind(type).fields, WHOLE) || {}, slideKind(type).fields);
    const slide = { slideType: type, content };
    if (slideWords(slide) > 0) out.push(slide);
  }
  return out.length ? out : null;
}

export function stripSlideBlocks(text) {
  return stripLabelledBlock(stripLabelledBlock(text, SLIDE_BLOCK), PROPOSAL_BLOCK);
}


/* ── The store ─────────────────────────────────────────────────────────────── */

export const PRESENTATION_FIELDS = ["name", "type", "presenter", "design", "skin", "offerId", "status", "sortOrder"];
export const DECK_SECTION_FIELDS = ["presentationId", "name", "brief", "sortOrder"];
export const SLIDE_FIELDS_STORED = ["presentationId", "sectionId", "slideType", "content", "sortOrder"];

const usePresentationRecords = createRecordStore({ table: "presentations", fields: PRESENTATION_FIELDS, idPrefix: "prs", orderBy: "sort_order", ascending: true });
const useSectionRecords = createRecordStore({ table: "presentation_sections", fields: DECK_SECTION_FIELDS, idPrefix: "pss", orderBy: "sort_order", ascending: true });
const useSlideRecords = createRecordStore({ table: "presentation_slides", fields: SLIDE_FIELDS_STORED, idPrefix: "psl", orderBy: "sort_order", ascending: true });

/** Renumber a list 0..n-1 in the given order; only the rows that move. */
function renumber(list) {
  return list.map((r, i) => ({ ...r, sortOrder: i })).filter((r, i) => list[i].sortOrder !== i);
}

/** The writes for dragging a slide from `from` to `to` inside one section. */
export function slideMoveWrites(sectionSlides = [], from, to) {
  if (from === to || from < 0 || to < 0 || from >= sectionSlides.length || to >= sectionSlides.length) return [];
  const next = [...sectionSlides];
  next.splice(to, 0, next.splice(from, 1)[0]);
  return renumber(next);
}

export function usePresentations() {
  const P = usePresentationRecords();
  const S = useSectionRecords();
  const L = useSlideRecords();

  const bySection = useMemo(() => {
    const map = new Map();
    for (const s of S.rows || []) map.set(s.presentationId, [...(map.get(s.presentationId) || []), s]);
    return map;
  }, [S.rows]);
  const byDeck = useMemo(() => {
    const map = new Map();
    for (const s of L.rows || []) map.set(s.presentationId, [...(map.get(s.presentationId) || []), s]);
    return map;
  }, [L.rows]);

  const sectionsFor = useCallback((id) => [...(bySection.get(id) || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)), [bySection]);
  const slidesFor = useCallback((id) => byDeck.get(id) || [], [byDeck]);

  /** A new deck of a type, its sections, and a Title and Closing slide at the
   *  two ends (every deck opens and closes that way — the spec). Written one row
   *  at a time; recordStore replaces rows one by one. */
  const addPresentation = useCallback(async (typeId) => {
    const type = presentationTypeDef(typeId);
    const decks = P.rows || [];
    const last = decks.length ? Math.max(...decks.map((d) => d.sortOrder ?? 0)) : -1;
    const deck = await P.save({ id: P.newId(), name: "", type: type?.id || null, presenter: "", design: "editorial", skin: {}, status: "draft", sortOrder: last + 1 });
    if (!deck) return null;
    const made = [];
    for (const [i, [name, brief]] of (type?.sections || []).entries()) {
      const s = await S.save({ id: S.newId(), presentationId: deck.id, name, brief, sortOrder: i });
      if (s) made.push(s);
    }
    if (made.length) {
      await L.save({ id: L.newId(), presentationId: deck.id, sectionId: made[0].id, slideType: "title", content: emptySlideContent("title"), sortOrder: 0 });
      await L.save({ id: L.newId(), presentationId: deck.id, sectionId: made[made.length - 1].id, slideType: "closing", content: emptySlideContent("closing"), sortOrder: 1000 });
    }
    return deck;
  }, [P, S, L]);

  const addSection = useCallback(async (deck) => {
    const list = sectionsFor(deck.id);
    const last = list.length ? Math.max(...list.map((s) => s.sortOrder ?? 0)) : -1;
    return S.save({ id: S.newId(), presentationId: deck.id, name: "", brief: "", sortOrder: last + 1 });
  }, [sectionsFor, S]);

  /** Slides at the end of a section — one blank one, or Brill's proposal once
   *  she has added it. Before a Closing slide that sits last, so the deck still
   *  ends on its ask. */
  const addSlides = useCallback(async (deck, section, newSlides) => {
    const mine = groupSlides(sectionsFor(deck.id), slidesFor(deck.id)).find((g) => g.section?.id === section.id)?.slides || [];
    const closingLast = mine.length && mine[mine.length - 1].slideType === "closing" ? mine[mine.length - 1] : null;
    const before = closingLast ? mine.slice(0, -1) : mine;
    const saved = [];
    let order = before.length;
    for (const s of newSlides) {
      const row = await L.save({ id: L.newId(), presentationId: deck.id, sectionId: section.id, slideType: s.slideType, content: s.content || emptySlideContent(s.slideType), sortOrder: order++ });
      if (row) saved.push(row);
    }
    if (closingLast && closingLast.sortOrder !== order) await L.save({ ...closingLast, sortOrder: order });
    return saved;
  }, [sectionsFor, slidesFor, L]);

  const reorderSlides = useCallback(async (deck, section, from, to) => {
    const mine = groupSlides(sectionsFor(deck.id), slidesFor(deck.id)).find((g) => g.section?.id === section.id)?.slides || [];
    let ok = true;
    for (const row of slideMoveWrites(mine, from, to)) if (!(await L.save(row))) ok = false;
    return ok;
  }, [sectionsFor, slidesFor, L]);

  const reorderSections = useCallback(async (deck, from, to) => {
    const list = sectionsFor(deck.id);
    let ok = true;
    for (const row of slideMoveWrites(list, from, to)) if (!(await S.save(row))) ok = false;
    return ok;
  }, [sectionsFor, S]);

  /** A section and its slides. The table cascades; the local lists do not. */
  const deleteSection = useCallback(async (section) => {
    for (const s of slidesFor(section.presentationId).filter((x) => x.sectionId === section.id)) {
      if (!(await L.remove(s.id))) return false;
    }
    return S.remove(section.id);
  }, [slidesFor, L, S]);

  const deletePresentation = useCallback(async (id) => {
    for (const s of slidesFor(id)) if (!(await L.remove(s.id))) return false;
    for (const s of sectionsFor(id)) if (!(await S.remove(s.id))) return false;
    return P.remove(id);
  }, [slidesFor, sectionsFor, L, S, P]);

  const reorderPresentations = useCallback(async (from, to) => {
    const list = [...(P.rows || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    let ok = true;
    for (const row of slideMoveWrites(list, from, to)) if (!(await P.save(row))) ok = false;
    return ok;
  }, [P]);

  return {
    presentations: P.rows || [],
    loading: P.loading || S.loading || L.loading,
    error: P.error || S.error || L.error,
    clearError: () => { P.clearError(); S.clearError(); L.clearError(); },
    sectionsFor, slidesFor,
    savePresentation: P.save, addPresentation, deletePresentation, reorderPresentations,
    saveSection: S.save, addSection, deleteSection, reorderSections,
    saveSlide: L.save, addSlides, removeSlide: L.remove, reorderSlides,
  };
}
